import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiChevronRight, FiChevronDown, FiLayers } from 'react-icons/fi';
import { useSolution } from '../../context/SolutionContext';
import ComponentCard from './ComponentCard';
import { useCompatibility } from '../../hooks/useCompatibility';

const LayerAccordion = ({ phase }) => {
  const { state, dispatch } = useSolution();
  const { getScore } = useCompatibility(state.selectedComponents);
  const [expandedLayers, setExpandedLayers] = useState(new Set());
  const [expandedSubLayers, setExpandedSubLayers] = useState(new Set());
  
  // Use state.expandedNodes for phase expansion
  const isPhaseExpanded = state.expandedNodes?.has(phase.id) ?? true;
  
  const toggleLayer = useCallback((layerId) => {
    setExpandedLayers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(layerId)) {
        newSet.delete(layerId);
      } else {
        newSet.add(layerId);
      }
      return newSet;
    });
  }, []);
  
  const toggleSubLayer = useCallback((subLayerId) => {
    setExpandedSubLayers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(subLayerId)) {
        newSet.delete(subLayerId);
      } else {
        newSet.add(subLayerId);
      }
      return newSet;
    });
  }, []);
  
  const togglePhase = useCallback(() => {
    dispatch({ type: 'TOGGLE_EXPAND', payload: phase.id });
  }, [dispatch, phase.id]);
  
  const filterComponents = (components) => {
    if (!state.searchQuery) return components;
    const query = state.searchQuery.toLowerCase();
    return components.filter(comp =>
      comp.name.toLowerCase().includes(query) ||
      comp.purpose?.toLowerCase().includes(query) ||
      comp.function?.toLowerCase().includes(query) ||
      comp.tags?.some(tag => tag.toLowerCase().includes(query))
    );
  };
  
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      {/* Phase Header */}
      <button
        onClick={togglePhase}
        className="w-full flex items-center justify-between p-3 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className="text-xl">{phase.icon}</span>
          <div className="text-left">
            <h3 className="font-semibold text-gray-900 text-sm">{phase.name}</h3>
            <p className="text-xs text-gray-500">{phase.description}</p>
          </div>
        </div>
        {isPhaseExpanded ? <FiChevronDown className="w-4 h-4" /> : <FiChevronRight className="w-4 h-4" />}
      </button>
      
      {/* Phase Content */}
      <AnimatePresence>
        {isPhaseExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="border-t border-gray-100">
              {phase.layers?.map(layer => {
                const isLayerExpanded = expandedLayers.has(layer.id);
                
                return (
                  <div key={layer.id} className="border-b border-gray-50 last:border-b-0">
                    {/* Layer Header */}
                    <button
                      onClick={() => toggleLayer(layer.id)}
                      className="w-full flex items-center justify-between p-2.5 pl-6 hover:bg-blue-50 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <FiLayers className="w-3.5 h-3.5 text-blue-500" />
                        <span className="font-medium text-gray-800 text-sm">{layer.name}</span>
                      </div>
                      {isLayerExpanded ? <FiChevronDown className="w-3.5 h-3.5" /> : <FiChevronRight className="w-3.5 h-3.5" />}
                    </button>
                    
                    {/* Layer Content */}
                    <AnimatePresence>
                      {isLayerExpanded && (
                        <motion.div
                          initial={{ height: 0 }}
                          animate={{ height: 'auto' }}
                          exit={{ height: 0 }}
                          transition={{ duration: 0.2 }}
                          className="overflow-hidden"
                        >
                          <div className="pb-2">
                            {layer.subLayers?.map(subLayer => {
                              const isSubLayerExpanded = expandedSubLayers.has(subLayer.id);
                              const filteredComponents = filterComponents(subLayer.components || []);
                              
                              if (state.searchQuery && filteredComponents.length === 0) return null;
                              
                              return (
                                <div key={subLayer.id} className="mt-1">
                                  {/* Sub-Layer Header */}
                                  <button
                                    onClick={() => toggleSubLayer(subLayer.id)}
                                    className="w-full flex items-center justify-between p-2 pl-10 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                                  >
                                    <div className="text-left">
                                      <span className="font-medium text-gray-700 text-sm">{subLayer.name}</span>
                                      <p className="text-xs text-gray-500">{subLayer.meaning}</p>
                                    </div>
                                    {isSubLayerExpanded ? <FiChevronDown className="w-3.5 h-3.5" /> : <FiChevronRight className="w-3.5 h-3.5" />}
                                  </button>
                                  
                                  {/* Components */}
                                  <AnimatePresence>
                                    {isSubLayerExpanded && (
                                      <motion.div
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: 'auto', opacity: 1 }}
                                        exit={{ height: 0, opacity: 0 }}
                                        transition={{ duration: 0.2 }}
                                        className="overflow-hidden"
                                      >
                                        <div className="grid grid-cols-1 gap-2 mt-1 pl-4 pr-2">
                                          {filteredComponents.map(component => {
                                            const enrichedComponent = {
                                              ...component,
                                              phaseId: phase.id,
                                              phaseName: phase.name,
                                              phaseIcon: phase.icon,
                                              layerId: layer.id,
                                              layerName: layer.name,
                                              subLayerId: subLayer.id,
                                              subLayerName: subLayer.name,
                                              subLayerMeaning: subLayer.meaning
                                            };
                                            
                                            return (
                                              <ComponentCard
                                                key={component.id}
                                                component={enrichedComponent}
                                                isSelected={state.selectedComponents?.some(c => c.id === component.id)}
                                                compatibilityScore={getScore(enrichedComponent)}
                                              />
                                            );
                                          })}
                                        </div>
                                      </motion.div>
                                    )}
                                  </AnimatePresence>
                                </div>
                              );
                            })}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LayerAccordion;